import type { Middleware } from '@floating-ui/react'

interface AvoidSiblingsOptions {
  getRects: () => DOMRect[]
  padding?: number
}

const CANDIDATES: Array<'top' | 'bottom' | 'left' | 'right'> = ['top', 'bottom', 'right', 'left']

interface Rect {
  x: number
  y: number
  width: number
  height: number
}

function overlapArea(a: Rect, b: Rect): number {
  const x = Math.max(0, Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x))
  const y = Math.max(0, Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y))
  return x * y
}

function candidateRect(side: 'top' | 'bottom' | 'left' | 'right', anchor: Rect, floating: { width: number; height: number }, padding: number): Rect {
  switch (side) {
    case 'top':
      return { x: anchor.x + anchor.width / 2 - floating.width / 2, y: anchor.y - floating.height - padding, width: floating.width, height: floating.height }
    case 'bottom':
      return { x: anchor.x + anchor.width / 2 - floating.width / 2, y: anchor.y + anchor.height + padding, width: floating.width, height: floating.height }
    case 'left':
      return { x: anchor.x - floating.width - padding, y: anchor.y + anchor.height / 2 - floating.height / 2, width: floating.width, height: floating.height }
    case 'right':
      return { x: anchor.x + anchor.width + padding, y: anchor.y + anchor.height / 2 - floating.height / 2, width: floating.width, height: floating.height }
  }
}

function clampToViewport(rect: Rect, vw: number, vh: number, pad: number): Rect {
  return {
    x: Math.max(pad, Math.min(vw - rect.width - pad, rect.x)),
    y: Math.max(pad, Math.min(vh - rect.height - pad, rect.y)),
    width: rect.width,
    height: rect.height,
  }
}

export function avoidSiblings({ getRects, padding = 12 }: AvoidSiblingsOptions): Middleware {
  return {
    name: 'avoidSiblings',
    fn({ x, y, rects, elements }) {
      const zones = getRects()
      if (zones.length === 0) return { x, y }
      const anchor = rects.reference
      const floating = rects.floating
      const vw = window.innerWidth
      const vh = window.innerHeight

      const current: Rect = { x, y, width: floating.width, height: floating.height }
      const currentOverlap = zones.reduce((acc, r) => acc + overlapArea(current, r), 0)
      if (currentOverlap <= 0.5) return { x, y }

      let best: { rect: Rect; overlap: number; side: string } | null = null
      for (const side of CANDIDATES) {
        const raw = candidateRect(side, anchor, floating, padding)
        const clamped = clampToViewport(raw, vw, vh, 8)
        const overlap = zones.reduce((acc, r) => acc + overlapArea(clamped, r), 0)
        if (!best || overlap < best.overlap) best = { rect: clamped, overlap, side }
      }
      if (best && best.overlap < currentOverlap) {
        elements.floating.setAttribute('data-avoided', best.side)
        return { x: best.rect.x, y: best.rect.y }
      }
      return { x, y }
    },
  }
}

